/**
 * Desktop pictures. The built-in ones are drawn from the theme variables and
 * the application's colour, so each works in light AND dark; "custom" is the
 * user's own image, kept on the server (user memory `__shell_wallpaper__`) and
 * shrunk here first so it stays well inside the server's limit.
 */
import { SHELL_LIMITS } from '../../nav/config'
import { S } from '../../nav/tokens'

export interface WallpaperPreset {
  id: string
  label: string
  css: (accent: string) => string
}

const mix = (a: string, pct: number, b = 'transparent') => `color-mix(in srgb, ${a} ${pct}%, ${b})`

export const WALLPAPERS: WallpaperPreset[] = [
  {
    id: 'theme', label: 'Application colour',
    css: (accent) => [
      `radial-gradient(1100px 680px at 88% 112%, ${mix(accent, 22)}, transparent 62%)`,
      `radial-gradient(820px 560px at -6% 28%, ${mix(accent, 13)}, transparent 64%)`,
      `linear-gradient(168deg, ${mix(accent, 9, S.bg)} 0%, ${S.bg} 72%)`,
    ].join(', '),
  },
  {
    id: 'aurora', label: 'Aurora',
    css: (accent) => [
      `radial-gradient(900px 520px at 15% 10%, ${mix('var(--color-success, #1a7f37)', 22)}, transparent 60%)`,
      `radial-gradient(900px 600px at 85% 20%, ${mix(accent, 26)}, transparent 62%)`,
      `radial-gradient(1000px 700px at 50% 120%, ${mix('var(--color-info, #0969da)', 20)}, transparent 60%)`,
      `${S.bg}`,
    ].join(', '),
  },
  {
    id: 'dusk', label: 'Dusk',
    css: (accent) => [
      `radial-gradient(1200px 700px at 100% 0%, ${mix('var(--color-warning, #9a6700)', 20)}, transparent 60%)`,
      `radial-gradient(1000px 800px at 0% 100%, ${mix(accent, 24)}, transparent 62%)`,
      `linear-gradient(200deg, ${mix(accent, 10, S.bg)}, ${S.bg})`,
    ].join(', '),
  },
  {
    id: 'meadow', label: 'Meadow',
    css: () => [
      `radial-gradient(1100px 600px at 20% 110%, ${mix('var(--color-success, #1a7f37)', 26)}, transparent 62%)`,
      `linear-gradient(180deg, ${mix('var(--color-info, #0969da)', 10, S.bg)} 0%, ${S.bg} 70%)`,
    ].join(', '),
  },
  {
    id: 'graphite', label: 'Graphite',
    css: () => [
      `radial-gradient(1000px 700px at 70% 30%, ${mix(S.text, 7)}, transparent 65%)`,
      `linear-gradient(160deg, ${mix(S.text, 5, S.bg)}, ${S.bg})`,
    ].join(', '),
  },
  { id: 'plain', label: 'Plain', css: () => S.bg },
]

export function wallpaperCss(choice: string, accent: string, image: string | null): string {
  if (choice === 'custom' && image) {
    return `center / cover no-repeat url("${image.replace(/"/g, '%22')}"), ${S.bg}`
  }
  return (WALLPAPERS.find((w) => w.id === choice) || WALLPAPERS[0]).css(accent)
}

/** Only these reach the server (it refuses anything else — SVG can carry script). */
const ACCEPTED = ['image/jpeg', 'image/png', 'image/webp']

/**
 * A picture the user chose, made desktop-sized: at most 2560×1600, JPEG, and
 * under the server's byte limit (quality stepped down until it fits). Rejects
 * with a message a person can act on.
 */
export async function prepareWallpaper(file: File): Promise<string> {
  if (!ACCEPTED.includes(file.type)) {
    throw new Error('Choose a JPEG, PNG or WebP picture.')
  }
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('That file could not be read as a picture.'))
      el.src = url
    })
    const scale = Math.min(1, 2560 / img.naturalWidth, 1600 / img.naturalHeight)
    const w = Math.max(1, Math.round(img.naturalWidth * scale))
    const h = Math.max(1, Math.round(img.naturalHeight * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('This browser cannot prepare the picture.')
    ctx.drawImage(img, 0, 0, w, h)
    // base64 inflates by 4/3; leave room for the data-URL prefix.
    const maxChars = Math.floor(SHELL_LIMITS.wallpaperMaxBytes * 4 / 3) - 64
    for (const q of [0.86, 0.78, 0.7, 0.6, 0.5, 0.4]) {
      const data = canvas.toDataURL('image/jpeg', q)
      if (data.length <= maxChars) return data
    }
    throw new Error('That picture is too detailed to keep — try a smaller one.')
  } finally {
    URL.revokeObjectURL(url)
  }
}
